/**
 * Touch-only virtual joystick. Mounts a fixed pad on the bottom-right of the
 * viewport. Anchors on touchstart inside the pad and tracks until release.
 * Calls `onMove(x, y)` where x/y are normalized to [-1, 1] (y up=positive).
 */
export class VirtualJoystick {
  private container: HTMLElement;
  private base: HTMLDivElement;
  private knob: HTMLDivElement;
  private radius = 56; // pad inner radius in px
  private active = false;
  private origin = { x: 0, y: 0 };
  private vec = { x: 0, y: 0 };
  private touchId: number | null = null;

  constructor(parent: HTMLElement, private onMove: (x: number, y: number) => void) {
    this.container = parent;
    this.base = document.createElement("div");
    this.base.className = "vj-base";
    this.knob = document.createElement("div");
    this.knob.className = "vj-knob";
    this.base.appendChild(this.knob);
    parent.appendChild(this.base);

    // Only show on coarse-pointer devices (touch); harmless on desktop where
    // it stays display:none via CSS media query.
    this.base.addEventListener("touchstart", this.start, { passive: false });
    this.base.addEventListener("touchmove", this.move, { passive: false });
    this.base.addEventListener("touchend", this.end);
    this.base.addEventListener("touchcancel", this.end);
  }

  dispose() {
    this.base.remove();
  }

  private start = (e: TouchEvent) => {
    e.preventDefault();
    const t = e.changedTouches[0];
    this.touchId = t.identifier;
    const r = this.base.getBoundingClientRect();
    this.origin.x = r.left + r.width / 2;
    this.origin.y = r.top + r.height / 2;
    this.active = true;
    this.update(t.clientX, t.clientY);
  };

  private move = (e: TouchEvent) => {
    if (!this.active) return;
    e.preventDefault();
    const t = Array.from(e.changedTouches).find((x) => x.identifier === this.touchId);
    if (!t) return;
    this.update(t.clientX, t.clientY);
  };

  private end = () => {
    this.active = false;
    this.touchId = null;
    this.vec.x = this.vec.y = 0;
    this.knob.style.transform = "translate(-50%, -50%)";
    this.onMove(0, 0);
  };

  private update(cx: number, cy: number) {
    let dx = cx - this.origin.x;
    let dy = cy - this.origin.y;
    const len = Math.hypot(dx, dy);
    const max = this.radius;
    if (len > max) {
      dx = (dx / len) * max;
      dy = (dy / len) * max;
    }
    this.knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    let nx = dx / max;
    let ny = -dy / max; // invert: screen y down → world y up
    // Non-linear response curve. Linear input + ship accel=70 made small
    // finger jitters read as full thrust on touch; user feedback was
    // "faster but less controllable". Reshape magnitude to m^1.7 — gentle
    // taper near center for fine control, while m=1 still maps to 1 so the
    // full-thrust corner is honest. m² felt dead; m^1.5 felt too linear;
    // m^1.7 is the middle ground (at m=0.5 output ≈ 0.31, vs linear 0.5).
    // Edge-snap at 90% so the outer ring still snaps to 1.0 for thrust.
    const mag = Math.hypot(nx, ny);
    if (mag > 0.9) {
      nx /= mag;
      ny /= mag;
    } else if (mag > 0.0001) {
      const curved = Math.pow(mag, 1.7);
      nx = (nx / mag) * curved;
      ny = (ny / mag) * curved;
    }
    this.vec.x = nx;
    this.vec.y = ny;
    this.onMove(nx, ny);
  }
}
